<script>
  import { _ } from "./i18n.js";
  import { onMount, onDestroy } from "svelte";
  import { getContext } from "svelte";
  import {
    f7,
    Page,
    Navbar,
    NavRight,
    Link,
    Searchbar,
    Sheet,
    Toolbar,
    PageContent,
    BlockTitle,
    Block,
    Button,
    Icon
  } from "framework7-svelte";
  import Map from "./Map.svelte";
  import {
    initAutoComplete,
    isDeliverable,
    getPlaceDetails,
    getAddressFromPlaceDetails,
    reverseGeocode
  } from "./MapService.js";
  import {
    createBuilding,
    createMoo,
    createStreet,
    createSubdistrict,
    createDistrict,
    createProvince,
    createZipcode,
    createCountry,
    createDeliverable,
    createDeliveryLocation,
    dirty,
    map
  } from "./stores.js";
  import { sendDeliveryRequest } from "./AddressService.js";

  let sheet;

  export let f7router;
  export let building;
  export let moo;
  export let street;
  export let subdistrict;
  export let district;
  export let province;
  export let zipcode;
  export let country;
  export let deliverable;
  export let deliveryLocation;

  if (!building) {
    building = createBuilding("");
  }
  if (!moo) {
    moo = createMoo("");
  }
  if (!street) {
    street = createStreet("");
  }
  if (!subdistrict) {
    subdistrict = createSubdistrict("");
  }
  if (!district) {
    district = createDistrict("");
  }
  if (!province) {
    province = createProvince($_("address.defaultProvince"));
  }
  if (!zipcode) {
    zipcode = createZipcode("");
  }
  if (!country) {
    country = createCountry($_("address.defaultCountry"));
  }
  if (!deliverable) {
    deliverable = createDeliverable();
  }
  if (!deliveryLocation) {
    deliveryLocation = createDeliveryLocation();
  }

  $: mapService = $map ? new google.maps.places.PlacesService($map) : null;

  let beforeunloadListener = function(e) {
    if ($dirty) {
      e.preventDefault();
    }
  };

  onMount(async () => {
    sheet = f7.sheet.create({ el: ".location-sheet" });
    window.addEventListener("beforeunload", beforeunloadListener);
  });

  onDestroy(async () => {
    window.removeEventListener("beforeunload", beforeunloadListener);
  });

  function onPageInit() {
    let searchbarAutocomplete;
    var searchbar = f7.searchbar.create({
      el: ".page-current #searchbar-autocomplete",
      customSearch: true,
      closeOnSelect: true,
      on: {
        enable: function(searchbar) {
          setTimeout(() => {
            if (app.theme === "ios") {
              searchbar.$el.css("overflow", "visible");
            } else {
              searchbar.$el.parents(".navbar-inner").css("overflow", "visible");
            }

            if (searchbarAutocomplete) {
              searchbarAutocomplete.open();
            } else {
              initAutoComplete({}, function(value) {
                searchbar.disable();

                let location = value.location;
                isDeliverable(location).then(function(result) {
                  deliverable.set(result);

                  deliveryLocation.set({
                    ...location,
                    showInfo: true,
                    checkLocation: true
                  });
                });
                $map.panTo({
                  lat: location.lat,
                  lng: location.lng
                });
              }).then(function(result) {
                searchbarAutocomplete = result;
                searchbarAutocomplete.open();
              });
            }
          }, 400);
        },
        disable: function(searchbar) {
          if (app.theme === "ios") {
            searchbar.$el.css("overflow", "");
          } else {
            searchbar.$el.parents(".navbar-inner").css("overflow", "");
          }
        }
      }
    });
  }

  function onPageOut() {
    sheet.close();
  }
</script>

<style>
  :global(body) {
    padding: 0;
  }
</style>

<Page on:pageAfterIn={onPageInit} on:pageAfterOut={onPageOut}>
  <Navbar
    title={$_('order.map.setDeliveryLocation')}
    backLink={$_('base.back')}>
    <NavRight>
      <Link
        searchbarEnable=".page-current .searchbar"
        icon="icon icon-search" />
    </NavRight>
    <Searchbar
      class="searchbar"
      placeholder={$_('location.searchbar.placeholder')}
      id="searchbar-autocomplete"
      init={false}
      expandable={true} />
  </Navbar>

  <Map
    style="z-index: -1"
    {sheet}
    {f7router}
    {deliverable}
    {deliveryLocation} />

  <Sheet
    class="location-sheet"
    style="height: auto; --f7-sheet-bg-color: #fff"
    push={false}
    backdrop={false}>
    <Toolbar>
      <div class="left" />
      <div class="right">
        <Link sheetClose>{$_('base.close')}</Link>
      </div>
    </Toolbar>
    <PageContent>
      <div
        style="display: flex; align-items: center; justify-content:
        space-between">
        <div>
          <BlockTitle medium>
            {$deliverable ? $_('location.deliverable.title') : $_('location.undeliverable.title')}
          </BlockTitle>
          <Block style="margin-top: 0px; margin-bottom: 0px">
            {$deliverable ? $_('location.deliverable.description') : $_('location.undeliverable.description')}
          </Block>
        </div>
        <div style="margin-right: 20px; justify-content: center;">
          {#if $deliverable}
            <Icon
              size="24px"
              style="color: green"
              icon="icon icon-deliverable" />
          {:else}
            <Icon
              size="24px"
              style="color: red"
              icon="icon icon-undeliverable" />
          {/if}
        </div>
      </div>
      <div class="padding-horizontal padding-bottom">
        {#if !$deliverable}
          <Button
            style="margin: 20px;"
            color="red"
            fill
            round
            small
            on:click={function() {
              sendDeliveryRequest('https://www.google.com/maps/search/' + $deliveryLocation.lat + ',' + $deliveryLocation.lng).then(
                function() {
                  sheet.close();
                }
              );
            }}>
            {$_('location.button.deliveryRequest')}
            <Icon color="white" style="color: white" icon="icon icon-line" />
          </Button>
        {:else}
          <Button
            style="margin: 20px;"
            fill
            round
            small
            on:click={function() {
              sheet.close();
              f7router.back();
            }}>
            {$_('location.button.useLocation')}
            <Icon icon="icon icon-location" />
          </Button>
          <Button
            style="margin: 20px;"
            color="green"
            fill
            round
            small
            on:click={function() {
              reverseGeocode($deliveryLocation)
                .then(function(place) {
                  sheet.close();
                  let changed = false;
                  if (place) {
                    let address = getAddressFromPlaceDetails(place);
                    changed = address.subdistrict.length > 0 && address.district.length > 0 && address.province.length > 0;
                    if (changed) {
                      building.set(address.building);
                      moo.set(address.moo);
                      street.set(address.street);
                      subdistrict.set(address.subdistrict);
                      district.set(address.district);
                      province.set(address.province);
                      zipcode.set(address.zipcode);
                      f7router.back();
                    }
                  }
                  if (!changed) {
                    f7.toast
                      .create({
                        text: $_('location.noAddressFound'),
                        position: 'bottom',
                        closeTimeout: 4000
                      })
                      .open();
                  }
                })
                .catch(function(error) {
                  f7.toast
                    .create({
                      text: $_('location.reverseGeocode.error'),
                      position: 'bottom',
                      closeTimeout: 4000
                    })
                    .open();
                });
            }}>
            {$_('location.button.useAddress')}
            <Icon icon="icon icon-address" />
          </Button>
        {/if}
      </div>
    </PageContent>
  </Sheet>
</Page>
